// react
import { useCallback, useMemo, useRef } from 'react';

// material ui
import { alpha, useTheme } from '@mui/material/styles';

import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { DateTime } from 'luxon';

// Little util
const toMs = (ts) => (typeof ts === 'number' ? ts : new Date(ts).getTime());
const ZONE = 'Europe/Copenhagen';

const EnergySumChartInteractive = ({
  points = [],
  selections,
  onChange,
  activeTool,
  title = 'Energy (Sum)',
  height = 380,
}) => {
  const chartRef = useRef(null);
  const theme = useTheme();

  // Derive palette-based colors
  const palette = theme.palette;
  const textPrimary = palette.text.primary;
  const textSecondary = palette.text.secondary;
  const divider = palette.divider;
  const bg = palette.mode === 'dark' ? 'transparent' : 'transparent'; // let the parent Paper drive it
  const grid = alpha(divider, 0.1);

  // Series + accent colors
  const seriesColor =
    palette.mode === 'dark' ? palette.info.light : palette.info.main;
  const baselineColor = palette.info.main;
  const comparisonColor = palette.success.main;
  const baselineBand = alpha(baselineColor, 0.16);
  const comparisonBand = alpha(comparisonColor, 0.16);

  // Normalize/prepare series
  const seriesData = useMemo(() => {
    return (Array.isArray(points) ? points : null)
      .filter(
        (p) =>
          p && p.timestamp && typeof p.sum === 'number' && !Number.isNaN(p.sum),
      )
      .map((p) => [toMs(p.timestamp), Number(p.sum)])
      .sort((a, b) => a[0] - b[0]);
  }, [points]);

  // Local derived visuals from selections -> plotLines/plotBands
  const { plotLines, plotBands } = useMemo(() => {
    const pl = [];
    const pb = [];

    const addLine = (value, text, color, { insideLeft = false } = {}) => {
      if (typeof value !== 'number') return;

      // For a vertical line:
      // - align: 'left'   -> label sits to the left of the line
      // - align: 'right'  -> label sits to the right of the line
      // So for a "to" (right edge) we want it INSIDE the band => left side of the line.
      const x = insideLeft ? -35 : 6; // small padding inside the band

      pl.push({
        value,
        width: 2,
        dashStyle: 'ShortDash',
        color,
        zIndex: 6,
        label: {
          text,
          rotation: 0,
          y: 14,
          align: insideLeft ? 'left' : null,
          x, // small nudge so it doesn’t hug the line
          // Optional helpers if you have labels at the extreme chart edge:
          overflow: 'justify',
          crop: false,
          style: { fontSize: '13px', fontWeight: 600, color: textSecondary },
        },
      });
    };

    const addBand = (from, to, text, color) => {
      if (typeof from === 'number' && typeof to === 'number' && to > from) {
        pb.push({
          from,
          to,
          color,
          zIndex: 3,
          label: {
            text,
            y: 14,
            style: { fontSize: '13px', fontWeight: 600, color: textSecondary },
          },
        });
      }
    };

    // Baseline
    const b = selections?.baseline;
    const c = selections?.comparison;
    // Baseline
    addLine(b?.start, 'Start', baselineColor); // inside right of the left edge
    addLine(b?.end, 'Slut', baselineColor, { insideLeft: true }); // inside left of the right edge
    addBand(b?.start, b?.end, '', baselineBand);

    // Comparison
    addLine(c?.start, 'Start', comparisonColor);
    addLine(c?.end, 'Slut', comparisonColor, { insideLeft: true });
    addBand(c?.start, c?.end, '', comparisonBand);

    return { plotLines: pl, plotBands: pb };
    // include palette-derived colors in deps so theme changes re-render
  }, [
    selections,
    baselineColor,
    comparisonColor,
    baselineBand,
    comparisonBand,
    textSecondary,
  ]);

  // Helpers to set points/ranges based on tool
  const setPoint = useCallback(
    (ts) => {
      if (!onChange) return;
      const clickedDayStart = DateTime.fromMillis(ts, { zone: ZONE })
        .startOf('day')
        .toMillis();
      const clickedDayEnd = DateTime.fromMillis(ts, { zone: ZONE })
        .endOf('day')
        .toMillis();
      const toSingleOrRangeSelection = (selection = {}) => {
        if (!selection.start || selection.end) {
          return { start: clickedDayStart, end: undefined };
        }

        const existingDayStart = DateTime.fromMillis(selection.start, {
          zone: ZONE,
        })
          .startOf('day')
          .toMillis();
        const existingDayEnd = DateTime.fromMillis(selection.start, {
          zone: ZONE,
        })
          .endOf('day')
          .toMillis();

        return {
          start: Math.min(existingDayStart, clickedDayStart),
          end: Math.max(existingDayEnd, clickedDayEnd),
        };
      };
      const next = {
        baseline: { ...selections?.baseline },
        comparison: { ...selections?.comparison },
      };

      if (activeTool === 'baseline-point') {
        next.baseline = toSingleOrRangeSelection(next.baseline);
      } else if (activeTool === 'comparison-point') {
        next.comparison = toSingleOrRangeSelection(next.comparison);
      }
      onChange(next);
    },
    [activeTool, onChange, selections],
  );

  const setRange = useCallback(
    (from, to) => {
      if (!onChange) return;
      const start = DateTime.fromMillis(Math.min(from, to), {
        zone: ZONE,
      }).startOf('day');
      const end = DateTime.fromMillis(Math.max(from, to), {
        zone: ZONE,
      }).endOf('day');
      const next = {
        baseline: { ...(selections?.baseline || {}) },
        comparison: { ...(selections?.comparison || {}) },
      };

      if (activeTool === 'baseline-range') {
        next.baseline = { start: start.toMillis(), end: end.toMillis() };
      } else if (activeTool === 'comparison-range') {
        next.comparison = { start: start.toMillis(), end: end.toMillis() };
      }
      onChange(next);
    },
    [activeTool, onChange, selections],
  );

  // Chart options (theme-aware)
  const options = useMemo(
    () => ({
      chart: {
        height,
        accessibilty: {
          enabled: false,
        },
        zoomType: 'x',
        panning: true,
        panKey: 'shift',
        backgroundColor: bg,
        style: {
          fontFamily:
            theme.typography?.fontFamily || 'Inter, Roboto, Arial, sans-serif',
        },
        events: {
          click: function (e) {
            if (!activeTool) return;
            if (activeTool.endsWith('point') && e && e.xAxis && e.xAxis[0]) {
              const ts = e.xAxis[0].value;
              setPoint(ts);
            }
          },
          selection: function (e) {
            if (!activeTool) return true; // allow normal zoom when no tool active
            if (activeTool.endsWith('range')) {
              if (e.xAxis && e.xAxis[0]) {
                const { min, max } = e.xAxis[0];
                setRange(min, max);
              }
              return false; // cancel zoom
            }
            return true;
          },
        },
      },
      title: {
        text: title,
        style: { color: textPrimary, fontWeight: 600, fontSize: '14px' },
      },
      credits: { enabled: false },
      time: { timezone: 'Europe/Copenhagen' },
      xAxis: {
        type: 'datetime',
        labels: { style: { color: textSecondary } },
        plotLines,
        plotBands,
        crosshair: {
          width: 1,
          color: alpha(textSecondary, 0.5), // or any color you like
          dashStyle: 'ShortDot', // optional
        },
      },
      yAxis: {
        title: { text: 'Sum', style: { color: textSecondary } },
        labels: {
          style: { color: textSecondary },
          formatter() {
            return Highcharts.numberFormat(this.value, 0);
          },
        },
        gridLineColor: grid,
      },
      legend: {
        enabled: false,
        itemStyle: { color: textPrimary },
        itemHiddenStyle: { color: textSecondary },
      },
      tooltip: {
        shared: true,
        xDateFormat: '%d-%m-%Y %H:%M',
        backgroundColor: palette.background.paper,
        borderColor: divider,
        style: { color: textPrimary },
        pointFormatter() {
          return `<span style="font-weight:600">${Highcharts.numberFormat(this.y, 2)}</span>`;
        },
      },
      plotOptions: {
        series: {
          states: {
            hover: { enabled: true, lineWidthPlus: 0 },
            inactive: { opacity: 0.6 },
          },
        },
      },
      series: [
        {
          type: 'line',
          name: 'Sum',
          data: seriesData,
          color: seriesColor,
          lineWidth: 2,
          marker: { enabled: false },
        },
      ],
    }),
    [
      height,
      bg,
      theme.typography?.fontFamily,
      title,
      textPrimary,
      textSecondary,
      plotLines,
      plotBands,
      grid,
      palette.background.paper,
      divider,
      seriesData,
      seriesColor,
      activeTool,
      setPoint,
      setRange,
    ],
  );

  return (
    <HighchartsReact ref={chartRef} highcharts={Highcharts} options={options} />
  );
};

export default EnergySumChartInteractive;
